import { Cell } from '@table-library/react-table-library/table';
import numeral from 'numeral';
import Sparkline from '../../../../../components/Sparklines';
import { showPlatform } from '../../../PromptManager/keys';
import { AI_TOOLS } from '../../../configs';
import Prompts from '../../../projects/components/prompts';

function getName(url) {
    try {
        const parsedUrl = new URL(url);
        const parts = parsedUrl.pathname.split('/').filter(Boolean);
        return parts.length ? parts[parts.length - 1] : parsedUrl.hostname;
    } catch (error) {
        return url;
    }
}

// Inline styles keep the row height capped without depending on the SCSS build.
const TITLE_WRAP_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    maxWidth: '100%',
};
const TITLE_TEXT_STYLE = {
    fontWeight: 700,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
};
const URL_LINK_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    maxWidth: '100%',
    color: '#667085',
    fontWeight: 500,
    whiteSpace: 'nowrap',
};
const URL_TEXT_STYLE = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};
function TitleUrlCell({ item }) {
    const title = item.title && item.title !== 'None' ? item.title : getName(item.url);
    return (
        <div style={TITLE_WRAP_STYLE}>
            <span style={TITLE_TEXT_STYLE} title={title}>
                {title}
            </span>
            <a
                target="_blank"
                style={URL_LINK_STYLE}
                href={item.url}
                rel="noreferrer"
                title={item.url}
            >
                <span style={URL_TEXT_STYLE}>{item.url}</span>
                <i className="icon-external-link" style={{ flex: '0 0 auto' }}></i>
            </a>
        </div>
    );
}

function SparklineCell({ series, loading, keyName, prefix, url, reverseY }) {
    if (loading) {
        return <span style={{ color: '#98a2b3', letterSpacing: '2px' }}>…</span>;
    }
    if (!series?.length) return <span>-</span>;

    // A single data point renders as an empty line, so show the value instead.
    if (series.length === 1) {
        return <strong>{numeral(series[0].value).format('0,0.[00]')}</strong>;
    }

    return (
        <Sparkline
            key={`${prefix}-${url}-${series.length}`}
            dataL={series.map((point) => point.value)}
            labels={series.map((point) => point.date)}
            keyName={keyName}
            reverseY={reverseY}
            titleType={(date) => `DATE: ${date}`}
            customDataType={(value) => numeral(value).format('0,0')}
        />
    );
}

export const COLUMNS = (t) => [
    {
        label: t('ai_tracking.keyword_detail.citations_vs_gsc.columns.title_url'),
        key: 'url',
        data: 'url',
        Component: Cell,
        sortType: 'string',
        pinLeft: true,
        cellSize: 'minmax(280px, 3fr)',
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.url;
            return <TitleUrlCell item={item} />;
        },
    },
    {
        label: t('ai_tracking.keyword_detail.citations_vs_gsc.columns.position'),
        key: 'position',
        data: 'avgPosition',
        Component: Cell,
        sortType: 'number',
        cellSize: 'minmax(180px, 2fr)',
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.avgPosition ?? '';
            return (
                <SparklineCell
                    series={item.positionSeries}
                    loading={!item.gscLoaded}
                    keyName={t('ai_tracking.keyword_detail.citations_vs_gsc.columns.position')}
                    prefix="position"
                    url={item.url}
                    reverseY
                />
            );
        },
    },
    {
        label: t('ai_tracking.keyword_detail.citations_vs_gsc.columns.platforms'),
        key: 'platforms',
        data: 'platformsCount',
        Component: Cell,
        sortType: 'number',
        cellSize: 'minmax(160px, 1.4fr)',
        renderCell: ({ item, csvDownload }) => {
            const platforms = item.platformsList ?? [];
            if (csvDownload) return platforms.join(', ');
            return (
                <div className="d-flex flex-wrap gap-4">
                    {Object.keys(AI_TOOLS).map((state) => (
                        <div
                            key={state}
                            title={AI_TOOLS[state]}
                            className={`ai-tracking-platform-2 no-clickable ${
                                platforms.includes(state) ? '' : 'disabled'
                            }`}
                        >
                            {showPlatform(state)}
                        </div>
                    ))}
                </div>
            );
        },
    },
    {
        label: t('ai_tracking.keyword_detail.citations_vs_gsc.columns.prompts'),
        key: 'prompts',
        data: 'promptsCount',
        Component: Cell,
        sortType: 'number',
        cellSize: 'minmax(180px, 1.8fr)',
        renderCell: ({ item, csvDownload }) => {
            const promptsList = item.promptsList ?? [];
            if (csvDownload) return promptsList.join(', ');
            if (!promptsList.length) return t('ai_tracking.projects.labels.noData');
            return (
                <Prompts
                    project={{}}
                    count={1}
                    trancuateSize={27}
                    showShortPrompts
                    noLink
                    data={promptsList.map((prompt) => ({ prompt }))}
                />
            );
        },
    },
    {
        label: t('ai_tracking.keyword_detail.citations_vs_gsc.columns.gsc_clicks'),
        key: 'gsc_clicks',
        data: 'gscClicksTotal',
        Component: Cell,
        sortType: 'number',
        cellSize: 'minmax(180px, 2fr)',
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.gscClicksTotal ?? '';
            return (
                <SparklineCell
                    series={item.gscClicks}
                    loading={!item.gscLoaded}
                    keyName={t('ai_tracking.keyword_detail.citations_vs_gsc.columns.gsc_clicks')}
                    prefix="clicks"
                    url={item.url}
                />
            );
        },
    },
    {
        label: t('ai_tracking.keyword_detail.citations_vs_gsc.columns.gsc_impressions'),
        key: 'gsc_impressions',
        data: 'gscImpressionsTotal',
        Component: Cell,
        sortType: 'number',
        cellSize: 'minmax(180px, 2fr)',
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return item.gscImpressionsTotal ?? '';
            return (
                <SparklineCell
                    series={item.gscImpressions}
                    loading={!item.gscLoaded}
                    keyName={t(
                        'ai_tracking.keyword_detail.citations_vs_gsc.columns.gsc_impressions',
                    )}
                    prefix="impressions"
                    url={item.url}
                />
            );
        },
    },
];
